import router from '@/router'

/**
 * HTTP methods enum
 */
export enum HttpMethod {
    GET = 'GET',
    POST = 'POST',
    PUT = 'PUT',
    DELETE = 'DELETE',
    PATCH = 'PATCH',
}

/**
 * API error types
 */
export enum ApiErrorType {
    NETWORK = 'network_error',
    AUTH = 'authentication_error',
    VALIDATION = 'validation_error',
    SERVER = 'server_error',
    NOT_FOUND = 'not_found',
    TIMEOUT = 'timeout_error',
    UNKNOWN = 'unknown_error',
}

/**
 * Custom error class for API errors
 */
export class ApiError extends Error {
    type: ApiErrorType
    status: number
    code?: string
    data?: any

    constructor(
        message: string,
        type: ApiErrorType = ApiErrorType.UNKNOWN,
        status = 0,
        code?: string,
        data?: any
    ) {
        super(message)
        this.name = 'ApiError'
        this.type = type
        this.status = status
        this.code = code
        this.data = data
    }
}

/**
 * API response type
 */
export interface ApiResponse<T = any> {
    data?: T
    error?: string
    errorCode?: number
    success: boolean
}

/**
 * Fetch options
 */
export interface FetchOptions {
    method: HttpMethod
    endpoint: string
    body?: any
    timeout?: number
    retries?: number
    retryDelay?: number
}

/**
 * Enhanced fetch function with proper typing, error handling, and timeout support
 * @param options - The fetch configuration options
 * @returns Promise that resolves to the API response
 * @throws ApiError when request fails
 */
export default async function fetchData<T = any>(options: FetchOptions): Promise<T> {
    const {
        method,
        endpoint,
        body,
        timeout = 30000, // Default timeout 30s
        retries = 3, // Default 3 retries
        retryDelay = 1000, // Default 1s base delay
    } = options

    const requestOptions: RequestInit = {
        method,
        ...(body && { body: typeof body === 'string' ? body : JSON.stringify(body) }),
        headers: new Headers({
            'Content-Type': 'application/json',
            Accept: 'application/json',
        }),
    }

    // Create timeout controller
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), timeout)
    requestOptions.signal = controller.signal

    // Function to handle a single fetch attempt
    const attemptFetch = async (attempt: number): Promise<T> => {
        try {
            const response = await fetch(`/api/v1/${endpoint}`, requestOptions)

            // Clear timeout since response was received
            clearTimeout(timeoutId)

            // Handle common HTTP status codes
            if (response.status === 200 || response.status === 201) {
                // For binary responses
                if (response.headers.get('Content-Type')?.includes('application/octet-stream')) {
                    return response.blob() as unknown as T
                }

                // For JSON responses
                const json = (await response.json()) as ApiResponse<T>

                // Check for API-level errors in successful responses
                if (json.error) {
                    throw new ApiError(
                        json.error,
                        ApiErrorType.SERVER,
                        response.status,
                        json.errorCode?.toString(),
                        json
                    )
                }

                // Return data if available, otherwise return the full response
                return (json.data !== undefined ? json.data : json) as T
            } else if (response.status === 401) {
                // Authentication error
                router.push('/')
                throw new ApiError(
                    'Unauthorized, please log in again',
                    ApiErrorType.AUTH,
                    response.status
                )
            } else if (response.status === 400) {
                // Validation error
                const json = await response.json()
                throw new ApiError(
                    json.error || 'Bad request',
                    ApiErrorType.VALIDATION,
                    response.status,
                    json.errorCode?.toString(),
                    json
                )
            } else if (response.status === 404) {
                throw new ApiError('Resource not found', ApiErrorType.NOT_FOUND, response.status)
            } else if (response.status >= 500) {
                // Server error - retryable
                if (attempt < retries) {
                    // Calculate exponential backoff with jitter
                    const delay = retryDelay * Math.pow(1.5, attempt) * (0.9 + Math.random() * 0.2)
                    await new Promise((resolve) => setTimeout(resolve, delay))
                    return attemptFetch(attempt + 1)
                }

                throw new ApiError('Server error', ApiErrorType.SERVER, response.status)
            }

            // Handle any other status code
            throw new ApiError(
                `Request failed with status: ${response.status}`,
                ApiErrorType.UNKNOWN,
                response.status
            )
        } catch (error: any) {
            // Clear timeout
            clearTimeout(timeoutId)

            // Handle abort/timeout
            if (error.name === 'AbortError') {
                throw new ApiError('Request timed out', ApiErrorType.TIMEOUT, 0)
            }

            // Handle connection/network errors - retryable
            if (
                error.message?.includes('NetworkError') ||
                error.message?.includes('Failed to fetch')
            ) {
                if (attempt < retries) {
                    const delay = retryDelay * Math.pow(1.5, attempt) * (0.9 + Math.random() * 0.2)
                    await new Promise((resolve) => setTimeout(resolve, delay))
                    return attemptFetch(attempt + 1)
                }

                throw new ApiError(
                    'Network error, please check your connection',
                    ApiErrorType.NETWORK,
                    0
                )
            }

            // If it's already an ApiError, rethrow
            if (error instanceof ApiError) {
                throw error
            }

            // Otherwise, create a new ApiError
            throw new ApiError(error.message || 'Unknown error occurred', ApiErrorType.UNKNOWN, 0)
        }
    }

    // Start the fetch chain
    return attemptFetch(0)
}
